import type {
	IAccordionModuleFields,
	IAnnouncementModuleFields,
	IAppModuleFields,
	IBannerFields,
	IContentCardModuleFields,
	IContentModuleFields,
	IMarketingSiteCombinedFields,
	IModuleContentOverBackgroundImageFields,
	IModuleFooterFields,
	IModuleFormFields,
	IModuleHeaderFields,
	IModuleIconRichTextListFields,
	IModuleImageFields,
	IModuleLogosFields,
	IModulePlanFields,
	IModuleTableFields,
	IModuleTitleRichTextFields,
	IHelpsiteArticleContentFields,
	IModuleUspFields,
	IPageModuleTextListFields,
	IPlanCarouselFields,
	IProgressModuleFields,
	IReviewModuleFields,
	IActionCardFields,
	IBannerModuleFields,
	IVeritreeCardFields,
	IVeritreeCardContainerFields,
	IModuleVeritreeMapFields,
	IModuleVeritreeTreesInForestFields,
} from "@types-contentful";
import React from "react";

import { useQrModalContext } from "../hooks";
import ContentfulAccordionModule from "./AccordionModule/AccordionModule";
import AnnouncementModule from "./AnnouncementModule";
import AppModule from "./AppModule";
import Banner from "./Banner";
import BannerCarousel from "./BannerCarousel";
import ContentCardModule from "./ContentCardModule";
import ContentModule from "./ContentModule";
import ContentfulModuleContentOverBackgroundImage from "./ContentOverBackgroundImageModule";
import FAQModule from "./FAQModule";
import FooterModule from "./FooterModule";
import FormModule from "./FormModule";
import HeaderModule from "./HeaderModule";
import IconAndTextModule from "./IconAndTextModule";
import IconRichTextListModule from "./IconRichTextListModule";
import ImageModule from "./ImageModule";
import LogoModuleNew from "./LogoModuleNew";
import PlanCarousel from "./PlanCarousel";
import PlanModule from "./PlanModule";
import ProgressModule from "./ProgressModule";
import QRModal from "./QRModal";
import ReviewModule from "./ReviewModule";
import TableModule from "./TableModule";
import TextListModule from "./TextListModule";
import TextModule from "./TextModule";
import ActionCard from "@componentsActionCard";
import VeritreeCard from "./VeritreeCard";
import VeritreeCardContainer from "./VeritreeCardContainer";
import VeritreeMap from "./VeritreeMap";
import TreesInForestModule from "./TreesInForestModule";
import type { IModuleFaqFields } from "@types-contentful";

type Props = {
	module: IMarketingSiteCombinedFields["modules"][0];
	id?: string;
	isSecondToLast?: boolean;
};

//@ts-ignore
const ContentModuleRenderer = ({ module, id, isSecondToLast }: Props) => {
	const contentTypeId = module.sys?.contentType?.sys?.id;
	const moduleId = module.sys?.id;
	const context = useQrModalContext();

	if (!context) {
		throw new Error("QR Modal context is not available!");
	}

	const { qrModalId } = context;

	if (qrModalId) {
		return (
			<QRModal
				{...(module.fields as IModulePlanFields)}
				qrData={{
					sys: { id: qrModalId },
				}}
			/>
		);
	}

	switch (contentTypeId) {
		case "banner":
			//@ts-ignore
			return <Banner {...(module.fields as IBannerFields)} id={id} />;
		case "bannerModule":
			//@ts-ignore
			return (
				<BannerCarousel {...(module.fields as IBannerModuleFields)} id={id} />
			);
		case "announcementModule":
			return (
				<AnnouncementModule {...(module.fields as IAnnouncementModuleFields)} />
			);
		case "moduleLogos":
			/* Carousel + all logos: import LogoModule and render <LogoModule {...} /> */
			return <LogoModuleNew {...(module.fields as IModuleLogosFields)} />;
		case "progressModule":
			return <ProgressModule {...(module.fields as IProgressModuleFields)} />;
		case "moduleFooter":
			return <FooterModule {...(module.fields as IModuleFooterFields)} />;
		case "contentModule":
			return <ContentModule {...(module.fields as IContentModuleFields)} />;
		case "reviewModule":
			return <ReviewModule {...(module.fields as IReviewModuleFields)} />;
		case "appModule":
			return <AppModule {...(module.fields as IAppModuleFields)} />;
		case "contentCardModule":
			return (
				<ContentCardModule {...(module.fields as IContentCardModuleFields)} />
			);
		case "pageModuleTextList":
			return (
				<TextListModule {...(module.fields as IPageModuleTextListFields)} />
			);
		case "moduleHeader":
			return <HeaderModule {...(module.fields as IModuleHeaderFields)} />;
		case "moduleContentOverBackgroundImage":
			return (
				<ContentfulModuleContentOverBackgroundImage
					{...(module.fields as IModuleContentOverBackgroundImageFields)}
				/>
			);
		case "moduleIconRichTextList":
			return (
				<IconRichTextListModule
					{...(module.fields as IModuleIconRichTextListFields)}
				/>
			);

		case "accordionModule":
			return (
				<ContentfulAccordionModule
					{...(module.fields as IAccordionModuleFields)}
				/>
			);
		case "moduleFaq":
			return <FAQModule {...(module.fields as IModuleFaqFields)} />;
		case "moduleText":
			return <TextModule {...module} />;
		case "moduleUsp":
			return <IconAndTextModule {...(module.fields as IModuleUspFields)} />;
		case "modulePlan":
			return (
				<PlanModule
					moduleId={moduleId}
					// module={module?.fields?.ctaButtonQrModal?.sys?.id}
					{...(module.fields as IModulePlanFields)}
				/>
			);
		case "moduleTable":
			return <TableModule {...(module.fields as IModuleTableFields)} />;
		case "planCarousel":
			return <PlanCarousel {...(module.fields as IPlanCarouselFields)} />;
		case "moduleForm":
			return <FormModule {...(module.fields as IModuleFormFields)} />;
		case "moduleImage":
			return <ImageModule {...(module.fields as IModuleImageFields)} />;
		case "actionCard":
			return <ActionCard {...(module.fields as IActionCardFields)} />;
		case "veritreeCard":
			return <VeritreeCard {...(module.fields as IVeritreeCardFields)} />;
		case "moduleVeritreeMap":
			return <VeritreeMap {...(module.fields as IModuleVeritreeMapFields)} />;
		case "veritreeCardContainer":
			return (
				<VeritreeCardContainer
					{...(module.fields as IVeritreeCardContainerFields)}
				/>
			);
		case "moduleVeritreeTreesInForest":
			return (
				<TreesInForestModule
					{...(module.fields as IModuleVeritreeTreesInForestFields)}
				/>
			);

		default:
			console.warn(`${contentTypeId || "unknown"} is not yet implemented`);
			return null;
	}
};

export default ContentModuleRenderer;
